import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { getActiveTenantId } from '@/lib/tenant';
import type {
  CreateKnowledgeBaseInput,
  KnowledgeBase,
  KnowledgeDocument,
  LinkKnowledgeBaseInput,
} from '@/lib/api/types';

// Statuses that mean ingestion is still running; the document list keeps
// polling (~2s) while any row is in one of them (spec 0014, FR-13).
const ACTIVE_DOCUMENT_STATUSES = new Set<string>(['pending', 'processing']);

const knowledgeBaseKeys = {
  all: () => ['knowledge-bases', getActiveTenantId()] as const,
  // The detail key is by `ref` -- a slug or a UUID (spec 0013 policy).
  detail: (ref: string) => ['knowledge-bases', getActiveTenantId(), ref] as const,
};

function documentsKey(baseRef: string) {
  return ['knowledge-documents', getActiveTenantId(), baseRef] as const;
}

function agentKnowledgeBasesKey(agentId: string | undefined) {
  return ['agent-knowledge-bases', getActiveTenantId(), agentId] as const;
}

// ----- Knowledge bases -----

export function useKnowledgeBases() {
  return useQuery({
    queryKey: knowledgeBaseKeys.all(),
    queryFn: () => apiFetch<KnowledgeBase[]>('/knowledge/bases'),
  });
}

/** Resolve a knowledge base by `ref` -- its slug or UUID (spec 0013 policy). */
export function useKnowledgeBase(ref: string | undefined) {
  return useQuery({
    queryKey: ref ? knowledgeBaseKeys.detail(ref) : ['knowledge-bases', 'detail', 'none'],
    queryFn: () => apiFetch<KnowledgeBase>(`/knowledge/bases/${ref}`),
    enabled: Boolean(ref),
  });
}

export function useCreateKnowledgeBase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateKnowledgeBaseInput) =>
      apiFetch<KnowledgeBase>('/knowledge/bases', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] });
    },
  });
}

export function useDeleteKnowledgeBase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ref: string) => apiFetch<void>(`/knowledge/bases/${ref}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] });
      void queryClient.invalidateQueries({ queryKey: ['knowledge-documents'] });
      // Base deletion cascades its agent links (spec 0014, FR-10).
      void queryClient.invalidateQueries({ queryKey: ['agent-knowledge-bases'] });
    },
  });
}

// ----- Documents -----

/**
 * Documents of one knowledge base, newest first. Polls every 2s while any
 * document is still pending/processing so status flips to ready/failed without
 * a manual refresh (spec 0014, AC-13).
 */
export function useDocuments(baseRef: string | undefined) {
  return useQuery({
    queryKey: baseRef ? documentsKey(baseRef) : ['knowledge-documents', 'none'],
    queryFn: () => apiFetch<KnowledgeDocument[]>(`/knowledge/bases/${baseRef}/documents`),
    enabled: Boolean(baseRef),
    refetchInterval: (query) =>
      query.state.data?.some((doc) => ACTIVE_DOCUMENT_STATUSES.has(doc.status)) ? 2000 : false,
  });
}

export function useUploadDocument(baseRef: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return apiFetch<KnowledgeDocument>(`/knowledge/bases/${baseRef}/documents`, {
        method: 'POST',
        body: form,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentsKey(baseRef) });
      // document_count on the base list/detail changes with the upload.
      void queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] });
    },
  });
}

export function useDeleteDocument(baseRef: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      apiFetch<void>(`/knowledge/documents/${documentId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentsKey(baseRef) });
      void queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] });
    },
  });
}

// ----- Agent links -----

export function useAgentKnowledgeBases(agentId: string | undefined) {
  return useQuery({
    queryKey: agentKnowledgeBasesKey(agentId),
    queryFn: () => apiFetch<KnowledgeBase[]>(`/agents/${agentId}/knowledge-bases`),
    enabled: Boolean(agentId),
  });
}

export function useLinkKnowledgeBase(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (knowledgeBaseId: string) => {
      const body: LinkKnowledgeBaseInput = { knowledge_base_id: knowledgeBaseId };
      return apiFetch<KnowledgeBase>(`/agents/${agentId}/knowledge-bases`, {
        method: 'POST',
        body,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentKnowledgeBasesKey(agentId) });
    },
  });
}

export function useUnlinkKnowledgeBase(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (knowledgeBaseId: string) =>
      apiFetch<void>(`/agents/${agentId}/knowledge-bases/${knowledgeBaseId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentKnowledgeBasesKey(agentId) });
    },
  });
}
